import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import Heading from '@atlaskit/heading';
import * as queryString from 'query-string';

import ChangeEmailConfirmationForm from '../components/ChangeEmailConfirmationForm';
import WhiteboxPage from '../components/WhiteboxPage';
import {
  changeEmailConfirmation,
  changeEmailRequestFailedAction,
} from '../actions/change-email-actions';
import {
  useButtonClickedEvent,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';

import messages from './EmailConfirmationFormPage.old.messages';
import { useForceRedirect } from '../utilities/hooks';
import { URLParameters } from '../types';
import { isIsolatedCloud } from '../utilities/env';

const pageId = 'EmailConfirmationPage';

export const EmailConfirmationFormPage = () => {
  const dispatch = useAppDispatch();

  const { email, newEmail, error, isLoading, redirectTo } = useAppSelector(
    state => state.changeEmail
  );

  const handleEmailChangeConfirmationSubmitClick = useButtonClickedEvent(
    pageId,
    'EmailConfirmationSubmit'
  );

  usePageViewedEvent(pageId);

  // These values are provided by SIS in the email that's sent to verify the user's email address.
  // We need to pass them in the URL of the API call.
  const { token, userId, source = '' }: URLParameters = queryString.parse(window.location.search);

  const handleSubmit = useCallback(
    (newEmail: string, password: string) => {
      // If the user gets here without the token - something went really wrong
      // This should have been caught by the initial page load
      if (!token) {
        dispatch(changeEmailRequestFailedAction('invalid_token'));

        return;
      }

      if (!userId) {
        dispatch(changeEmailRequestFailedAction('unknown_error'));

        return;
      }

      dispatch(changeEmailConfirmation(newEmail, password, token, userId, source));
    },
    [dispatch, source, token, userId]
  );

  const resetPasswordLink =
    '/login/resetpassword?' +
    new URLSearchParams([
      ['redirectedFrom', 'change-email'],
      ['email', email],
      ['continue', window.location.href],
    ]);

  const resetPasswordLinkEnabled = !isIsolatedCloud();

  // `redirectTo` is set in the success case of the email change flow.
  useForceRedirect({ redirectTo });

  return (
    <WhiteboxPage
      pageId={pageId}
      header={
        <Heading as={'h1'} size={'small'}>
          <FormattedMessage {...messages.title} />
        </Heading>
      }
      whiteboxFooter={
        resetPasswordLinkEnabled ? (
          <Link to={resetPasswordLink} id="resetPassword">
            <FormattedMessage
              id="having.trouble.logging.in"
              defaultMessage="Having trouble logging in?"
            />
          </Link>
        ) : null
      }>
      <ChangeEmailConfirmationForm
        email={newEmail}
        isLoading={isLoading}
        error={error}
        onSubmitButtonClick={handleEmailChangeConfirmationSubmitClick}
        onSubmit={handleSubmit}
      />
    </WhiteboxPage>
  );
};

export default EmailConfirmationFormPage;
